import { attrs, cx, html, type Html, type Part } from "../html.ts";
import { button } from "./actions.ts";

export interface FieldProps {
  id: string;
  label: string;
  name?: string;
  value?: string;
  placeholder?: string;
  type?: "text" | "email" | "url" | "search" | "password";
  multiline?: boolean;
  /** Mono for invite codes, paths and anything typed as code. */
  mono?: boolean;
  help?: Part;
  /** An inline error. Sets aria-invalid and links the message with aria-describedby. */
  error?: Part;
  required?: boolean;
  disabled?: boolean;
  autocomplete?: string;
  inputmode?: string;
}

/** bt-field with label, input and help or error line. */
export function field(props: FieldProps): Html {
  const helpId = `${props.id}-help`;
  const hasError = props.error !== undefined && props.error !== null && props.error !== false;
  const note = hasError ? props.error : props.help;
  const hasNote = note !== undefined && note !== null && note !== false;
  const common = {
    id: props.id,
    name: props.name,
    class: cx("bt-input", props.mono && "bt-mono"),
    placeholder: props.placeholder,
    required: props.required,
    disabled: props.disabled,
    autocomplete: props.autocomplete,
    inputmode: props.inputmode,
    "aria-invalid": hasError ? "true" : undefined,
    "aria-describedby": hasNote ? helpId : undefined,
  };
  const control = props.multiline
    ? html`<textarea${attrs(common)}>${props.value ?? ""}</textarea>`
    : html`<input${attrs({ ...common, type: props.type ?? "text", value: props.value })}>`;
  return html`<div class="${cx("bt-field", hasError && "is-invalid")}"><label class="bt-label" for="${props.id}">${props.label}</label>${control}${hasNote ? html`<span class="bt-help" id="${helpId}"${hasError ? attrs({ role: "alert" }) : ""}>${note}</span>` : ""}</div>`;
}

export interface CommandProps {
  text: string;
  prompt?: string;
  copyLabel?: string;
}

/** bt-cmd: a copyable one-liner. Wire the button with `wireCopy` from dom.ts. */
export function command(props: CommandProps): Html {
  const prompt = props.prompt ?? "$";
  return html`<div class="bt-cmd"><span class="bt-cmd__prompt" aria-hidden="true">${prompt}</span><code class="bt-cmd__text" tabindex="0">${props.text}</code>${button({
    label: props.copyLabel ?? "Copy",
    size: "sm",
    attrs: { "data-copy": props.text },
  })}</div>`;
}
